import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  styled,
} from "@mui/material";
import { RichText } from "../../../../components/RichText";
import { HatIcon } from "../../icons/HatIcon";
import type { AfterHighSchoolBlurb } from "../../../types";

interface BlurbDialogProps {
  open: boolean;
  onClose: () => void;
  blurb: AfterHighSchoolBlurb | null;
}

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
      stroke="#637381"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StyledDialog = styled(Dialog, {
  label: "BlurbDialog",
})(() => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    maxWidth: 560,
    width: "100%",
    boxShadow: "-40px 40px 80px -8px rgba(145, 158, 171, 0.24)",
  },
}));

const StyledDialogTitle = styled(DialogTitle, {
  label: "BlurbDialogTitle",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "32px 12px 32px 24px",
  height: 48,
}));

const StyledDialogContent = styled(DialogContent, {
  label: "BlurbDialogContent",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3),
}));

const StyledDialogActions = styled(DialogActions, {
  label: "BlurbDialogActions",
})(({ theme }) => ({
  justifyContent: "center",
  padding: theme.spacing(3),
  gap: theme.spacing(1.5),
}));

export const BlurbDialog = ({ open, onClose, blurb }: BlurbDialogProps) => {
  if (!blurb) return null;

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        <Box component="span" sx={{ display: "flex", flexShrink: 0 }}>
          <HatIcon width={40} height={40} />
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} sx={{ padding: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Typography
          sx={{
            fontFamily: "Proxima Nova",
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#1c252e",
          }}
        >
          {blurb.title}
        </Typography>

        {blurb.description != null && (
          <Box
            sx={{
              fontFamily: "Proxima Nova",
              fontSize: "18px",
              lineHeight: "28px",
              color: "#1c252e",
              "& strong, & b": {
                fontWeight: 600,
              },
              "& p": {
                margin: 0,
                marginBottom: 1,
              },
            }}
          >
            <RichText
              value={
                typeof blurb.description === "string"
                  ? blurb.description
                  : JSON.stringify(blurb.description)
              }
            />
          </Box>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: "#078dee",
            color: "#ffffff",
            fontFamily: "Proxima Nova",
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "24px",
            textTransform: "none",
            borderRadius: 1,
            minWidth: 64,
            height: 36,
            "&:hover": {
              backgroundColor: "#0570c4",
            },
          }}
        >
          Got it
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};
